import { parseAuthorizationContext } from '@/lib/access-policy'
import apiClient from '@/lib/api-client'
import { isPlatformRole } from '@/lib/role-assignment-policy'
import type {
  AuthorizationAuditEvent,
  AuthorizationContext,
  ParticipantProvisionRequest,
  PersonalGoal,
  PortalInvitationResponse,
  ProgramType,
  RoleAssignment,
  RoleGrantRequest,
  RoleKey,
  RoleRevokeRequest,
} from '@/types/access-control'
import { PERSONAL_GOALS, PROGRAM_TYPES, ROLE_KEYS } from '@/types/access-control'

function invalidContract(name: string): Error {
  return new Error(`Invalid ${name} response`)
}

interface ParsedRecord extends Record<string, unknown> {
  action?: unknown
  actor_user_id?: unknown
  created_at?: unknown
  event_metadata?: unknown
  expires_at?: unknown
  id?: unknown
  organization_id?: unknown
  primary_goal?: unknown
  program_type?: unknown
  reason?: unknown
  role?: unknown
  role_key?: unknown
  scope_type?: unknown
  target_user_id?: unknown
  user_id?: unknown
}

function isRecord(value: unknown): value is ParsedRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
}

function isNullableDate(value: unknown): value is string | null {
  return value === null || (typeof value === 'string' && !Number.isNaN(Date.parse(value)))
}

export function parseRoleAssignment(value: unknown): RoleAssignment | null {
  if (!isRecord(value)) return null
  const id = value.id
  const userId = value.user_id
  const roleValue = value.role
  const scopeType = value.scope_type
  const expiresAt = value.expires_at
  const organizationId = value.organization_id
  const programType = value.program_type
  if (
    !isPositiveInteger(id) ||
    !isPositiveInteger(userId) ||
    typeof roleValue !== 'string' ||
    !ROLE_KEYS.includes(roleValue as (typeof ROLE_KEYS)[number]) ||
    (scopeType !== 'platform' && scopeType !== 'organization') ||
    !isNullableDate(expiresAt) ||
    (programType !== undefined &&
      programType !== null &&
      (roleValue !== 'participant' ||
        typeof programType !== 'string' ||
        !PROGRAM_TYPES.includes(programType as ProgramType)))
  ) {
    return null
  }
  const role = roleValue as RoleKey
  if (isPlatformRole(role)) {
    if (scopeType !== 'platform' || organizationId !== null) return null
  } else if (scopeType !== 'organization' || !isPositiveInteger(organizationId)) {
    return null
  }
  return {
    id,
    user_id: userId,
    role,
    scope_type: scopeType,
    organization_id: organizationId as number | null,
    expires_at: expiresAt,
    ...(programType !== undefined ? { program_type: programType as ProgramType | null } : {}),
  }
}

function parseAuditEvent(value: unknown): AuthorizationAuditEvent | null {
  if (!isRecord(value)) return null
  const id = value.id
  const action = value.action
  const actorUserId = value.actor_user_id
  const targetUserId = value.target_user_id
  const roleKey = value.role_key
  const organizationId = value.organization_id
  const reason = value.reason
  const eventMetadata = value.event_metadata
  const createdAt = value.created_at
  if (
    !isPositiveInteger(id) ||
    typeof action !== 'string' ||
    (actorUserId !== null && !isPositiveInteger(actorUserId)) ||
    (targetUserId !== null && !isPositiveInteger(targetUserId)) ||
    (roleKey !== null && typeof roleKey !== 'string') ||
    (organizationId !== null && !isPositiveInteger(organizationId)) ||
    typeof reason !== 'string' ||
    !isRecord(eventMetadata) ||
    typeof createdAt !== 'string' ||
    Number.isNaN(Date.parse(createdAt))
  ) {
    return null
  }
  return {
    id,
    action,
    actor_user_id: actorUserId as number | null,
    target_user_id: targetUserId as number | null,
    role_key: roleKey as string | null,
    organization_id: organizationId as number | null,
    reason,
    event_metadata: eventMetadata,
    created_at: createdAt,
  }
}

export const accessControlService = {
  async getContext(): Promise<AuthorizationContext> {
    const response = await apiClient.get<unknown>('/api/v1/access-control/context')
    const context = parseAuthorizationContext(response.data)
    if (!context) throw invalidContract('authorization context')
    return context
  },

  async updateCurrentGoal(personalGoal: PersonalGoal): Promise<PersonalGoal> {
    const response = await apiClient.patch<unknown>('/api/v1/access-control/me/goal', {
      personal_goal: personalGoal,
    })
    if (
      !isRecord(response.data) ||
      typeof response.data.primary_goal !== 'string' ||
      !PERSONAL_GOALS.includes(response.data.primary_goal as PersonalGoal)
    ) {
      throw invalidContract('participant goal')
    }
    return response.data.primary_goal as PersonalGoal
  },

  async listUserRoles(userId: number, includeExpired = false): Promise<RoleAssignment[]> {
    const response = await apiClient.get<unknown>(`/api/v1/access-control/users/${userId}/roles`, {
      params: { include_expired: includeExpired },
    })
    if (!Array.isArray(response.data)) throw invalidContract('role assignments')
    const assignments = response.data.map(parseRoleAssignment)
    if (assignments.some((assignment) => assignment === null)) {
      throw invalidContract('role assignments')
    }
    return assignments as RoleAssignment[]
  },

  async sendPortalInvitation(userId: number): Promise<PortalInvitationResponse> {
    const response = await apiClient.post<PortalInvitationResponse>(
      `/api/v1/access-control/users/${userId}/portal-invitation`,
    )
    return response.data
  },

  async grantRole(userId: number, request: RoleGrantRequest): Promise<RoleAssignment> {
    const response = await apiClient.post<unknown>(
      `/api/v1/access-control/users/${userId}/roles`,
      request,
    )
    const assignment = parseRoleAssignment(response.data)
    if (!assignment) throw invalidContract('role assignment')
    return assignment
  },

  async provisionParticipant(userId: number, request: ParticipantProvisionRequest): Promise<void> {
    await apiClient.put(`/api/v1/access-control/users/${userId}/program/provision`, request)
  },

  async revokeRole(userId: number, role: RoleKey, request: RoleRevokeRequest): Promise<void> {
    await apiClient.delete(`/api/v1/access-control/users/${userId}/roles/${role}`, {
      data: request,
    })
  },

  async listAuditEvents(targetUserId?: number, limit = 100): Promise<AuthorizationAuditEvent[]> {
    const response = await apiClient.get<unknown>('/api/v1/access-control/audit-events', {
      params: { target_user_id: targetUserId, limit },
    })
    if (!Array.isArray(response.data)) throw invalidContract('authorization audit events')
    const events = response.data.map(parseAuditEvent)
    if (events.some((event) => event === null)) {
      throw invalidContract('authorization audit events')
    }
    return events as AuthorizationAuditEvent[]
  },
}
